import type { DigitalCenter } from 'shared';
import { PERIOD_PRESETS, type DayRange, type PeriodPreset } from '@/lib/reportPeriod';

interface ReportFiltersProps {
  preset: PeriodPreset;
  range: DayRange;
  centerId: number;
  centers: DigitalCenter[];
  /** The latest day that can be chosen: today. */
  today: string;
  onPreset: (preset: PeriodPreset) => void;
  onRange: (range: DayRange) => void;
  onCenter: (centerId: number) => void;
}

/**
 * What the report covers: one row, above everything, and everything below
 * answers to it.
 */
export default function ReportFilters({
  preset,
  range,
  centerId,
  centers,
  today,
  onPreset,
  onRange,
  onCenter,
}: ReportFiltersProps) {
  return (
    <div className="card p-4 flex flex-wrap items-end gap-x-5 gap-y-3">
      <div className="flex flex-col gap-1">
        <span id="report-period" className="text-xs font-medium text-muted-foreground">
          Período
        </span>
        <div className="flex rounded-md border border-border overflow-hidden" role="group" aria-labelledby="report-period">
          {PERIOD_PRESETS.map((p) => (
            <button
              key={p.value}
              type="button"
              aria-pressed={preset === p.value}
              onClick={() => onPreset(p.value)}
              className={`h-10 px-3 text-sm font-medium transition-colors border-l border-border first:border-l-0 ${
                preset === p.value ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-accent'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-1">
        <label htmlFor="report-from" className="text-xs font-medium text-muted-foreground">
          De
        </label>
        <input
          id="report-from"
          type="date"
          className="input w-40"
          value={range.from}
          max={today}
          onChange={(e) => e.target.value && onRange({ ...range, from: e.target.value })}
        />
      </div>

      <div className="flex flex-col gap-1">
        <label htmlFor="report-to" className="text-xs font-medium text-muted-foreground">
          Até
        </label>
        <input
          id="report-to"
          type="date"
          className="input w-40"
          value={range.to}
          max={today}
          onChange={(e) => e.target.value && onRange({ ...range, to: e.target.value })}
        />
      </div>

      <div className="flex flex-col gap-1 min-w-[220px]">
        <label htmlFor="report-centre" className="text-xs font-medium text-muted-foreground">
          Centro
        </label>
        <select
          id="report-centre"
          className="input"
          value={centerId}
          onChange={(e) => onCenter(Number(e.target.value))}
        >
          <option value={0}>Todos os centros</option>
          {centers.map((c) => (
            <option key={c.id} value={c.id}>
              {c.centerName}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}
